/**
 * RecentJobsPanel – list of the last N print/export jobs, persisted in
 * localStorage via `recentJobsStore`.
 *
 * Exports two components:
 *  - `RecentJobsHeader` — compact header button (History icon + count badge)
 *    that opens a dropdown panel; used in the app header bar.
 *  - `RecentJobsPanel` — standalone collapsible panel (kept for reuse).
 *
 * Each job row shows status, page count, and progress (0-100%) in the
 * bottom-right corner when `progress` is set.
 */

import * as React from "react";
import { History, Trash2, X, FileText, Archive, Printer, CheckCircle2, AlertTriangle, XCircle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useTranslation } from "@/i18n";
import {
  listRecentJobs,
  clearRecentJobs,
  removeRecentJob,
  RECENT_JOBS_EVENT,
  type RecentJob,
  type JobKind,
} from "@/utils/recentJobsStore";
import { cn } from "@/lib/utils";

const KIND_ICON: Record<JobKind, React.ComponentType<{ className?: string }>> = {
  "pdf-single": FileText,
  "pdf-all": FileText,
  "pdf-zip": Archive,
  print: Printer,
};

const STATUS_ICON = {
  ok: CheckCircle2,
  error: XCircle,
  partial: AlertTriangle,
} as const;

const STATUS_COLOR: Record<RecentJob["status"], string> = {
  ok: "text-emerald-600",
  error: "text-red-600",
  partial: "text-amber-600",
};

function formatRelative(ts: number): string {
  const diff = Date.now() - ts;
  const min = Math.floor(diff / 60000);
  if (min < 1) return "Vừa xong";
  if (min < 60) return `${min} phút trước`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h} giờ trước`;
  const d = Math.floor(h / 24);
  return `${d} ngày trước`;
}

/** Shared hook: jobs list, refreshed on focus / storage / same-tab events. */
function useJobs(): [RecentJob[], () => void, (id: string) => void] {
  const [jobs, setJobs] = React.useState<RecentJob[]>(() => listRecentJobs());

  React.useEffect(() => {
    const refresh = () => setJobs(listRecentJobs());
    window.addEventListener("focus", refresh);
    window.addEventListener("storage", refresh);
    window.addEventListener(RECENT_JOBS_EVENT, refresh);
    return () => {
      window.removeEventListener("focus", refresh);
      window.removeEventListener("storage", refresh);
      window.removeEventListener(RECENT_JOBS_EVENT, refresh);
    };
  }, []);

  const clearAll = React.useCallback(() => {
    clearRecentJobs();
    setJobs([]);
  }, []);

  const removeOne = React.useCallback((id: string) => {
    removeRecentJob(id);
    setJobs((prev) => prev.filter((j) => j.id !== id));
  }, []);

  return [jobs, clearAll, removeOne];
}

/** One job row with progress in the bottom-right corner. */
function JobRow({ job, onRemove }: { job: RecentJob; onRemove: (id: string) => void }) {
  const { t } = useTranslation();
  const KindIcon = KIND_ICON[job.kind];
  const StatusIcon = STATUS_ICON[job.status];
  const progress = job.progress ?? (job.status === "ok" ? 100 : 0);

  return (
    <li className="flex items-start gap-2 px-3 py-2 hover:bg-muted/40">
      <KindIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 text-[11px] font-medium">
          <StatusIcon className={`h-3 w-3 ${STATUS_COLOR[job.status]}`} />
          <span className="truncate">
            {job.pageCount} {t("common.pages")} · {job.sheetName || "—"}
          </span>
        </div>
        <p className="truncate text-[10px] text-muted-foreground" title={job.excelFile}>
          {job.excelFile}
        </p>
        {job.templateFile && (
          <p className="truncate text-[10px] text-muted-foreground" title={job.templateFile}>
            {t("recentJobs.template")}: {job.templateFile}
          </p>
        )}
        <div className="mt-0.5 flex items-center justify-between gap-2">
          <span className="text-[10px] text-muted-foreground">{formatRelative(job.startedAt)}</span>
          <span className="flex items-center gap-1">
            <span className="font-mono text-[10px] text-primary">
              {progress < 100 ? `${progress}%` : `✓ ${job.pageCount} trang`}
            </span>
            {job.note && (
              <span className="max-w-[9rem] truncate text-[10px] italic text-muted-foreground" title={job.note}>
                {job.note}
              </span>
            )}
            <button
              type="button"
              className="rounded p-0.5 text-muted-foreground hover:text-red-600"
              title={t("recentJobs.remove")}
              onClick={() => onRemove(job.id)}
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        </div>
        {/* Live progress bar while the job is still rendering/exporting. */}
        {progress < 100 && (
          <div
            className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-muted"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={progress}
          >
            <div
              className="h-full rounded-full bg-primary transition-all duration-300 ease-out"
              style={{ width: `${progress}%` }}
            />
          </div>
        )}
      </div>
    </li>
  );
}

/**
 * Header button + dropdown: History icon with count badge; clicking toggles
 * the jobs list. `className` lets the parent position the dropdown.
 */
export function RecentJobsHeader({ className }: { className?: string }) {
  const { t } = useTranslation();
  const [jobs, clearAll, removeOne] = useJobs();
  const [open, setOpen] = React.useState(false);
  const rootRef = React.useRef<HTMLDivElement>(null);

  // Close on outside click
  React.useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  return (
    <div ref={rootRef} className={cn("relative", className)}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="relative flex h-8 w-8 items-center justify-center rounded-md border border-border/70 bg-background/60 text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground"
        title={t("recentJobs.title")}
        aria-expanded={open}
      >
        <History className="h-4 w-4" />
        {jobs.length > 0 && (
          <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[9px] font-bold leading-none text-primary-foreground">
            {jobs.length}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-10 z-50 w-[22rem] max-w-[calc(100vw-2rem)] overflow-hidden rounded-lg border bg-background shadow-lg">
          <div className="flex items-center justify-between border-b px-3 py-2">
            <span className="flex items-center gap-1.5 text-xs font-semibold">
              <History className="h-3.5 w-3.5" />
              {t("recentJobs.title")}
              <Badge variant="secondary" className="ml-1 px-1.5 py-0 text-[10px]">
                {jobs.length}
              </Badge>
            </span>
            {jobs.length > 0 && (
              <button
                type="button"
                className="flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] text-muted-foreground hover:bg-muted hover:text-red-600"
                onClick={clearAll}
                title={t("recentJobs.clearAll")}
              >
                <Trash2 className="h-3 w-3" /> Xóa tất cả
              </button>
            )}
          </div>

          {jobs.length === 0 ? (
            <p className="px-3 py-6 text-center text-[11px] text-muted-foreground">
              {t("recentJobs.empty")}
            </p>
          ) : (
            <ul className="max-h-80 divide-y overflow-auto">
              {jobs.map((job) => (
                <JobRow key={job.id} job={job} onRemove={removeOne} />
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

/** Standalone collapsible panel (sidebar use). */
export function RecentJobsPanel() {
  const { t } = useTranslation();
  const [jobs, clearAll, removeOne] = useJobs();
  const [open, setOpen] = React.useState(false);

  if (jobs.length === 0 && !open) {
    return (
      <div className="rounded-lg border border-dashed p-3 text-center text-[11px] text-muted-foreground">
        <History className="mx-auto mb-1 h-4 w-4 opacity-60" />
        {t("recentJobs.empty")}
      </div>
    );
  }

  return (
    <div className="rounded-lg border bg-muted/20">
      <button
        type="button"
        className="flex w-full items-center justify-between px-3 py-2 text-left text-xs font-semibold hover:bg-muted/50"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span className="flex items-center gap-1.5">
          <History className="h-3.5 w-3.5" />
          {t("recentJobs.title")}
          <Badge variant="secondary" className="ml-1 px-1.5 py-0 text-[10px]">
            {jobs.length}
          </Badge>
        </span>
        <span className="flex items-center gap-1">
          {jobs.length > 0 && (
            <span
              role="button"
              tabIndex={0}
              className="cursor-pointer rounded p-0.5 text-muted-foreground hover:text-red-600"
              title={t("recentJobs.clearAll")}
              onClick={(e) => {
                e.stopPropagation();
                clearAll();
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  e.stopPropagation();
                  clearAll();
                }
              }}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </span>
          )}
          <X className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-0" : "rotate-45"}`} />
        </span>
      </button>

      {open && (
        <div className="max-h-96 overflow-auto border-t">
          {jobs.length === 0 ? (
            <p className="px-3 py-3 text-center text-[11px] text-muted-foreground">
              {t("recentJobs.empty")}
            </p>
          ) : (
            <ul className="divide-y">
              {jobs.map((job) => (
                <JobRow key={job.id} job={job} onRemove={removeOne} />
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
